// -----------------------------------------------------------------------------
// SimpleFieldPanel  (Wave C)
//
// Action panel body for FREE_TEXT, MULTIPLE_CHOICE, CURRENCY_AMOUNT,
// PAYMENT_METHOD, STAFF_PICKER, DATE_FIELD.
//
// Switches input type based on item.item_type. On submit it:
//   1. POSTs the value into the checklist item's response_payload via the
//      /complete/ endpoint ITSELF (self-contained, mirroring ConfirmationPanel /
//      UploadDocumentPanel) so the structured value persists. The orchestrator
//      should only refresh/advance UI after this succeeds.
//   2. ALSO posts a human-readable lead note ("<label>: <value>") as an audit
//      trail — harmless and useful for the timeline.
//   3. Calls onComplete?.(item) purely as a refresh signal.
//
// response_payload keys are the canonical per-item_type keys the BE validates
// against (apps/tasks/payload_schemas.py) and that TaskChecklistField.jsx writes:
//   FREE_TEXT → {text}   MULTIPLE_CHOICE → {choices:[...]}   CURRENCY_AMOUNT → {amount}
//   STAFF_PICKER → {staff_user_id}   DATE_FIELD → {date}
//   PAYMENT_METHOD → {payment_type} (the chosen method label; the full
//     payment-record mapping — payment_id/amount/mode_of_payment — is a separate
//     BE concern, see note in the panel)
// -----------------------------------------------------------------------------
import PropTypes from 'prop-types';
import { useState, useMemo } from 'react';
import {
    Box,
    Typography,
    Button,
    TextField,
    MenuItem,
    CircularProgress,
    Stack,
    RadioGroup,
    FormControlLabel,
    Radio,
    InputAdornment,
} from '@mui/material';
import { dispatch, useSelector } from 'src/redux/store';
import { updateError } from 'src/redux/slices/user';
import { callApi } from 'src/utils/apiCall';
import FilterAssigneeList from 'src/utils/filterAssigneeList';
import Iconify from 'src/components/Iconify';

// ---------------------------------------------------------------------------
// Input rendering per item_type
// ---------------------------------------------------------------------------

function FreeTextInput({ value, onChange }) {
    return (
        <TextField
            data-testid="simple-field-input"
            multiline
            rows={3}
            fullWidth
            size="small"
            placeholder="Enter your response…"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            autoFocus
        />
    );
}
FreeTextInput.propTypes = { value: PropTypes.string.isRequired, onChange: PropTypes.func.isRequired };

function MultipleChoiceInput({ item, value, onChange }) {
    // Options can come from item.options or item.raw?.options
    const options = item?.options || item?.raw?.options || [];
    if (options.length === 0) {
        return <FreeTextInput value={value} onChange={onChange} />;
    }
    return (
        <RadioGroup
            data-testid="simple-field-input"
            value={value}
            onChange={(e) => onChange(e.target.value)}
        >
            {options.map((opt, idx) => {
                const label = typeof opt === 'string' ? opt : opt.label || opt.value || String(idx);
                const val = typeof opt === 'string' ? opt : opt.value || opt.label || String(idx);
                return (
                    <FormControlLabel
                        key={val}
                        value={val}
                        control={<Radio size="small" sx={{ color: '#6e6e73', '&.Mui-checked': { color: '#078DEE' } }} />}
                        label={<Typography sx={{ fontSize: '13px' }}>{label}</Typography>}
                    />
                );
            })}
        </RadioGroup>
    );
}
MultipleChoiceInput.propTypes = { item: PropTypes.object, value: PropTypes.string.isRequired, onChange: PropTypes.func.isRequired };
MultipleChoiceInput.defaultProps = { item: null };

function CurrencyAmountInput({ value, onChange }) {
    return (
        <TextField
            data-testid="simple-field-input"
            type="number"
            size="small"
            placeholder="0.00"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            InputProps={{
                startAdornment: <InputAdornment position="start">£</InputAdornment>,
                inputProps: { min: 0, step: '0.01' },
            }}
            sx={{ width: 160 }}
        />
    );
}
CurrencyAmountInput.propTypes = { value: PropTypes.string.isRequired, onChange: PropTypes.func.isRequired };

function PaymentMethodInput({ value, onChange }) {
    const methods = ['Cash', 'Card', 'Bank transfer', 'Finance', 'Other'];
    return (
        <TextField
            data-testid="simple-field-input"
            select
            size="small"
            fullWidth
            value={value}
            onChange={(e) => onChange(e.target.value)}
            SelectProps={{ displayEmpty: true }}
        >
            <MenuItem value="">— Select method —</MenuItem>
            {methods.map((m) => <MenuItem key={m} value={m}>{m}</MenuItem>)}
        </TextField>
    );
}
PaymentMethodInput.propTypes = { value: PropTypes.string.isRequired, onChange: PropTypes.func.isRequired };

function StaffPickerInput({ value, onChange }) {
    const rawUsers = useSelector((state) => state.practiceUser?.practiceUser?.results || []);
    const assignees = useMemo(() => FilterAssigneeList(rawUsers), [rawUsers]);
    return (
        <TextField
            data-testid="simple-field-input"
            select
            size="small"
            fullWidth
            value={value}
            onChange={(e) => onChange(e.target.value)}
            SelectProps={{ displayEmpty: true }}
        >
            <MenuItem value="">— Select staff member —</MenuItem>
            {assignees.map((a) => <MenuItem key={a.id} value={String(a.id)}>{a.name}</MenuItem>)}
        </TextField>
    );
}
StaffPickerInput.propTypes = { value: PropTypes.string.isRequired, onChange: PropTypes.func.isRequired };

function DateFieldInput({ value, onChange }) {
    return (
        <TextField
            data-testid="simple-field-input"
            type="date"
            size="small"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ width: 200 }}
        />
    );
}
DateFieldInput.propTypes = { value: PropTypes.string.isRequired, onChange: PropTypes.func.isRequired };

// ---------------------------------------------------------------------------
// Meta
// ---------------------------------------------------------------------------

const TYPE_META = {
    FREE_TEXT:       { label: 'Free text',       icon: 'mdi:text',               color: '#6e6e73' },
    MULTIPLE_CHOICE: { label: 'Multiple choice',  icon: 'mdi:format-list-bulleted', color: '#5B8DEF' },
    CURRENCY_AMOUNT: { label: 'Amount',           icon: 'mdi:currency-gbp',        color: '#34c759' },
    PAYMENT_METHOD:  { label: 'Payment method',   icon: 'mdi:credit-card-outline', color: '#5BB5F5' },
    STAFF_PICKER:    { label: 'Staff member',     icon: 'mdi:account-outline',     color: '#FFAB00' },
    DATE_FIELD:      { label: 'Date',             icon: 'mdi:calendar-outline',    color: '#00B8D9' },
    SIMPLE_DATE:     { label: 'Set Date',         icon: 'mdi:calendar-outline',    color: '#00B8D9' },
};
const DEFAULT_META = { label: 'Response', icon: 'mdi:pencil-outline', color: '#6e6e73' };

// ---------------------------------------------------------------------------
// response_payload builder — one structured key per item_type, matching the BE
// schema (apps/tasks/payload_schemas.py) and the canonical TaskChecklistField
// cell writers. Falls back to a generic { value } for unknown types so the value
// is never silently lost (warn-only on the BE; never strict-rejected for these
// catalogued types).
// ---------------------------------------------------------------------------
function buildResponsePayload(itemType, rawValue) {
    switch (itemType) {
        case 'FREE_TEXT':
            return { text: String(rawValue).trim() };
        case 'MULTIPLE_CHOICE':
            // BE stores an array of option ids/labels under `choices`.
            return { choices: [rawValue] };
        case 'CURRENCY_AMOUNT': {
            const amount = parseFloat(rawValue);
            return { amount: Number.isFinite(amount) ? amount : 0 };
        }
        case 'PAYMENT_METHOD':
            // The simple method dropdown only captures the chosen method label.
            // The full payment-record mapping (payment_id / amount / mode_of_payment
            // via a treatments/payments POST) is intentionally OUT OF SCOPE here —
            // see the task note. payment_type is the allowed key for the label.
            return { payment_type: String(rawValue) };
        case 'STAFF_PICKER':
            // StaffPickerInput values are stringified user ids.
            return { staff_user_id: rawValue };
        case 'DATE_FIELD':
            // Native date input → 'YYYY-MM-DD'; BE `date` key.
            return { date: rawValue };
        default:
            return { value: rawValue };
    }
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function SimpleFieldPanel({
    item,
    practiceId,
    leadId,
    leadDetail,
    onComplete,
    onClose,
}) {
    const [value, setValue] = useState('');
    const [submitting, setSubmitting] = useState(false);

    const itemType = item?.item_type || '';
    const meta = TYPE_META[itemType] || DEFAULT_META;

    const renderInput = () => {
        switch (itemType) {
            case 'FREE_TEXT':        return <FreeTextInput value={value} onChange={setValue} />;
            case 'MULTIPLE_CHOICE':  return <MultipleChoiceInput item={item} value={value} onChange={setValue} />;
            case 'CURRENCY_AMOUNT':  return <CurrencyAmountInput value={value} onChange={setValue} />;
            case 'PAYMENT_METHOD':   return <PaymentMethodInput value={value} onChange={setValue} />;
            case 'STAFF_PICKER':     return <StaffPickerInput value={value} onChange={setValue} />;
            case 'DATE_FIELD':       return <DateFieldInput value={value} onChange={setValue} />;
            case 'SIMPLE_DATE':      return <DateFieldInput value={value} onChange={setValue} />;
            default:                 return <FreeTextInput value={value} onChange={setValue} />;
        }
    };

    const handleSubmit = async () => {
        if (!value) {
            dispatch(updateError({ success: false, message: 'Please enter a value' }));
            return;
        }
        setSubmitting(true);

        const taskId = item?.taskId ?? item?.rawTask?.id;
        const itemId = item?.itemId ?? item?.id;
        const treatmentId = leadDetail?.lead_treatments?.[0]?.id || null;
        const label = item?.title || meta.label;

        try {
            // 1. STRUCTURED PERSISTENCE — POST /complete/ ourselves with the typed
            //    response_payload so the value is recorded on the checklist item.
            //    Self-contained (mirrors ConfirmationPanel); the orchestrator
            //    should only refresh/advance UI after this succeeds.
            if (!practiceId || !taskId || !itemId) {
                // No real task (e.g. preview item) — nothing to persist structurally.
                // Just signal the orchestrator so the UI advances.
                setSubmitting(false);
                onComplete?.(item);
                return;
            }

            const completeRes = await callApi(
                `/api/v1/practices/${practiceId}/tasks/${taskId}/checklist/${itemId}/complete/`,
                'post',
                {
                    completion_state: 'YES',
                    response_payload: buildResponsePayload(itemType, value),
                }
            );

            if (!completeRes.ok) {
                dispatch(updateError({ success: false, message: completeRes?.data?.detail || 'Failed to save response' }));
                return;
            }

            // 2. AUDIT TRAIL — human-readable lead note (best-effort; a note failure
            //    must NOT undo the structured persistence above).
            try {
                await callApi(
                    `/api/v1/practices/${practiceId}/leads/${leadId}/notes/`,
                    'post',
                    {
                        lead: leadId,
                        // LeadNote API stores free text in `additional_notes` (`description` is ignored).
                        additional_notes: `${label}: ${value}`,
                        ...(treatmentId ? { treatment: treatmentId } : {}),
                    }
                );
            } catch {
                // Swallow — the value is already persisted in response_payload.
            }

            dispatch(updateError({ success: true, message: 'Response saved' }));
            // 3. Refresh signal — orchestrator reconciles + closes the panel.
            onComplete?.({ ...item, completionAlreadyPersisted: true });
        } catch (err) {
            dispatch(updateError({ success: false, message: err.message || 'Something went wrong' }));
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Box
            data-testid="simple-field-panel"
            sx={{
                p: 2,
                display: 'flex',
                flexDirection: 'column',
                gap: 1.75,
                height: '100%',
                overflowY: 'auto',
            }}
        >
            {/* Icon header */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                <Box
                    sx={{
                        width: 36,
                        height: 36,
                        borderRadius: '10px',
                        bgcolor: `${meta.color}1A`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                    }}
                >
                    <Iconify icon={meta.icon} width={18} height={18} sx={{ color: meta.color }} />
                </Box>
                <Box>
                    <Typography sx={{ fontSize: '14px', fontWeight: 600, color: '#1d1d1f', letterSpacing: '-0.012em' }}>
                        {item?.title || meta.label}
                    </Typography>
                    {item?.description && (
                        <Typography sx={{ fontSize: '12px', color: '#6e6e73', letterSpacing: '-0.005em' }}>
                            {item.description}
                        </Typography>
                    )}
                </Box>
            </Box>

            {/* Dynamic input */}
            <Box>
                <Typography sx={{ fontSize: '12px', fontWeight: 600, color: '#3c3c43', letterSpacing: '-0.005em', mb: 0.5 }}>
                    {meta.label}
                </Typography>
                {renderInput()}
            </Box>

            {/* Actions */}
            <Stack direction="row" spacing={1} sx={{ mt: 'auto' }}>
                <Button
                    data-testid="simple-field-submit"
                    variant="contained"
                    disableElevation
                    onClick={handleSubmit}
                    disabled={submitting || !value}
                    startIcon={submitting ? <CircularProgress size={14} sx={{ color: '#fff' }} /> : <Iconify icon="mdi:check" width={14} height={14} />}
                    sx={{
                        flex: 1,
                        textTransform: 'none',
                        borderRadius: '8px',
                        fontSize: '13px',
                        fontWeight: 600,
                        bgcolor: meta.color,
                        '&:hover': { filter: 'brightness(0.9)' },
                        boxShadow: 'none',
                    }}
                >
                    {submitting ? 'Saving…' : 'Save'}
                </Button>
                <Button
                    variant="outlined"
                    size="small"
                    onClick={onClose}
                    disabled={submitting}
                    sx={{ textTransform: 'none', borderRadius: '8px', fontSize: '13px' }}
                >
                    Cancel
                </Button>
            </Stack>
        </Box>
    );
}

SimpleFieldPanel.propTypes = {
    item: PropTypes.object,
    practiceId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    leadId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    leadDetail: PropTypes.object,
    onComplete: PropTypes.func,
    onClose: PropTypes.func.isRequired,
};

SimpleFieldPanel.defaultProps = {
    item: null,
    practiceId: null,
    leadId: null,
    leadDetail: null,
    onComplete: null,
};
